import { cn } from "@/lib/cn";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { type ComponentPropsWithoutRef, forwardRef } from "react";

export const ToastProvider = ToastPrimitive.Provider;
export const ToastAction = ToastPrimitive.Action;
export const ToastClose = ToastPrimitive.Close;

export const ToastViewport = forwardRef<
  HTMLOListElement,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    className={cn(
      "fixed bottom-0 right-0 z-50 flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:max-w-[380px]",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

type Tone = "default" | "success" | "warning" | "critical";

const TONE_CLASS: Record<Tone, string> = {
  default: "border-[var(--iris-line-2)]",
  success: "border-[var(--iris-neon-green)]/30",
  warning: "border-[var(--iris-neon-orange)]/30",
  critical: "border-[var(--iris-neon-pink)]/30",
};

interface ToastProps extends ComponentPropsWithoutRef<typeof ToastPrimitive.Root> {
  tone?: Tone;
}

export const Toast = forwardRef<HTMLLIElement, ToastProps>(
  ({ className, tone = "default", ...props }, ref) => (
    <ToastPrimitive.Root
      ref={ref}
      className={cn(
        "relative flex w-full items-start gap-3 overflow-hidden rounded-xl border p-4",
        "bg-[var(--iris-bg-3)] backdrop-blur-xl shadow-[var(--iris-shadow-raised)]",
        TONE_CLASS[tone],
        className,
      )}
      {...props}
    />
  ),
);
Toast.displayName = ToastPrimitive.Root.displayName;

export const ToastTitle = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title
    ref={ref}
    className={cn("text-sm font-medium text-[var(--iris-fg-1)]", className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitive.Title.displayName;

export const ToastDescription = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn("text-xs text-[var(--iris-fg-3)]", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitive.Description.displayName;
